import { GraphicPrimitive } from "./GraphicPrimitive";

export class ComplexPoly extends GraphicPrimitive {
    // contours is an array of rings (array of points {x, y})
    // The first ring is usually the outer boundary, subsequent rings are holes
    // However, polygon-clipping might return multipolygons (array of polygons).
    // For simplicity, we'll assume this class represents a MultiPolygon conceptual model:
    // a list of "Polygons", where each Polygon is a list of "Rings".
    // STRUCTURE: [ [[x,y], ...], [[x,y], ...] ] -> This is ONE Polygon with holes.
    // If we have MultiPolygon, we might need an array of these structures.
    // Let's stick to the polygon-clipping output format for "MultiPolygon":
    // [ [ [x,y], ... (ring) ], ... (polygon) ] ... (multipolygon)

    constructor(contours, color, fillColor, fillType, spacing, angle, segmentLength) {
        super(color, fillColor, fillType, spacing, angle, segmentLength, 'simple', 'ComplexPoly');
        this.contours = contours || []; // Expecting polygon-clipping MultiPolygon structure
    }

    toJSON() {
        return {
            ...super.toJSON(),
            contours: this.contours
        };
    }

    // Helper to get all points for control points (flattened)
    // For ComplexPoly, dragging individual points might be tricky, 
    // but maybe we just allow moving the whole shape for now.
    getControlPoints() {
        return [];
        // return this.contours.flat(2).map(p => ({x: p[0], y: p[1]}));
    }

    updateControlPoint(pointIndex, newX, newY) {
        // Implementation for editing vertices would go here
        this.regenerateStitches();
    }

    move(dx, dy) {
        // contours structure from polygon-clipping: Array<Polygon>
        // Polygon: Array<Ring>
        // Ring: Array<[x, y]>
        for (let poly of this.contours) {
            for (let ring of poly) {
                for (let point of ring) {
                    point[0] += dx;
                    point[1] += dy;
                }
            }
        }
        this.regenerateStitches();
    }

    regenerateStitches() {
        if (!this.fillColor || this.fillType !== 'parallel') {
            this.stitchLists = [];
            return;
        }

        // Calculate global bounds for MultiPolygon
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (let poly of this.contours) {
            for (let ring of poly) {
                for (let point of ring) {
                    if (point[0] < minX) minX = point[0];
                    if (point[0] > maxX) maxX = point[0];
                    if (point[1] < minY) minY = point[1];
                    if (point[1] > maxY) maxY = point[1];
                }
            }
        }

        if (minX === Infinity) {
            this.stitchLists = [];
            return;
        }

        const bounds = { minX, maxX, minY, maxY };
        this.generateParallelStitches(bounds, this.contours);
    }

    // Check if point is inside using even-odd rule via canvas api
    isPointInside(px, py, transform) {
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        const path = new Path2D();

        for (let poly of this.contours) {
            for (let ring of poly) {
                if (ring.length === 0) continue;
                path.moveTo(ring[0][0], ring[0][1]);
                for (let i = 1; i < ring.length; i++) {
                    path.lineTo(ring[i][0], ring[i][1]);
                }
                path.closePath();
            }
        }

        // Check fill
        if (tempCtx.isPointInPath(path, px, py, 'evenodd')) {
            return true;
        }

        // Check stroke
        const tolerance = 5 / transform.scale;
        tempCtx.lineWidth = (this.lineWidth + 10) / transform.scale;
        return tempCtx.isPointInStroke(path, px, py);
    }

    renderDrawing(ctx, transform) {
        ctx.save();
        ctx.beginPath();

        for (let poly of this.contours) {
            for (let ring of poly) {
                if (ring.length === 0) continue;
                ctx.moveTo(ring[0][0], ring[0][1]);
                for (let i = 1; i < ring.length; i++) {
                    ctx.lineTo(ring[i][0], ring[i][1]);
                }
                ctx.closePath();
            }
        }

        if (this.fillColor) {
            ctx.fillStyle = this.fillColor;
            ctx.fill('evenodd'); // Important for holes
        }

        ctx.strokeStyle = this.isSelected ? 'red' : this.color;
        ctx.lineWidth = this.lineWidth / transform.scale;
        ctx.stroke();
        ctx.restore();
    }

    // Helper to convert FROM [ {x,y}, ... ] TO [ [[x,y],...] ] (simple polygon conversion)
    static fromSimplePoly(points) {
        if (points.length < 3) return null;
        // Ensure closed
        const ring = points.map(p => [p.x, p.y]);
        if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
            ring.push([ring[0][0], ring[0][1]]);
        }
        // Return as MultiPolygon structure: [ [ ring ] ]
        return [[ring]];
    }
}
